import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Switch } from './ui/switch';
import { TrustBadges } from './TrustSignals';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, DollarSign, Package, Clock, Star, 
  TrendingUp, Sparkles, ChefHat, Utensils, Coffee, Loader2,
  Check, X, AlertCircle, BarChart3, RefreshCw, Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'appetizers', label: 'Appetizers', icon: Utensils },
  { value: 'mains', label: 'Main Dishes', icon: ChefHat },
  { value: 'sides', label: 'Sides', icon: Package },
  { value: 'drinks', label: 'Drinks', icon: Coffee },
  { value: 'desserts', label: 'Desserts', icon: Star },
  { value: 'specials', label: 'Specials', icon: Sparkles },
];

export default function FoodProviderDashboard() {
  const { getAuthHeaders } = useAuth();
  const [menuItems, setMenuItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('menu');
  const [showAddItem, setShowAddItem] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [aiUpdating, setAiUpdating] = useState(false);

  // New item form
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    category: 'mains',
    price: '',
    image_url: '',
    prep_time: 15,
    ai_price_enabled: false
  });

  // Quick add items
  const [quickAddItems, setQuickAddItems] = useState([
    { name: '', price: '', category: 'mains', ai_price_enabled: false }
  ]);

  useEffect(() => {
    fetchMenuItems();
    fetchOrders();
  }, []);

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/food/menu/items`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setMenuItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/food/orders`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/food/menu/item`, {
        ...newItem,
        price: parseFloat(newItem.price)
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Menu item added!');
      setShowAddItem(false);
      setNewItem({ name: '', description: '', category: 'mains', price: '', image_url: '', prep_time: 15, ai_price_enabled: false });
      fetchMenuItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const handleQuickAdd = async () => {
    const validItems = quickAddItems.filter(item => item.name && item.price);
    if (validItems.length === 0) {
      toast.error('Please add at least one item with name and price');
      return;
    }

    try {
      const response = await axios.post(`${API}/food/menu/quick-add`, {
        items: validItems.map(item => ({
          ...item,
          price: parseFloat(item.price)
        }))
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(`Added ${response.data.items_added} items!`);
      setShowQuickAdd(false);
      setQuickAddItems([{ name: '', price: '', category: 'mains', ai_price_enabled: false }]);
      fetchMenuItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add items');
    }
  };

  const handleUpdateItem = async () => {
    if (!editingItem) return;
    
    try {
      await axios.put(`${API}/food/menu/item/${editingItem.item_id}`, editingItem, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Item updated!');
      setEditingItem(null);
      fetchMenuItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this menu item?')) return;
    
    try {
      await axios.delete(`${API}/food/menu/item/${itemId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Item deleted');
      fetchMenuItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleAiPriceUpdate = async (itemIds = []) => {
    setAiUpdating(true);
    try {
      const response = await axios.post(`${API}/food/menu/ai-price-update`, {
        item_ids: itemIds
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      if (response.data.items_updated > 0) {
        toast.success(`AI updated ${response.data.items_updated} prices!`);
        fetchMenuItems();
      } else {
        toast.info('No price changes needed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI price update failed');
    } finally {
      setAiUpdating(false);
    }
  };

  const handleOrderStatusUpdate = async (orderId, status) => {
    try {
      await axios.put(`${API}/food/order/${orderId}/status`, { status }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(`Order ${status}`);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const addQuickAddRow = () => {
    setQuickAddItems([...quickAddItems, { name: '', price: '', category: 'mains', ai_price_enabled: false }]);
  };

  const updateQuickAddRow = (index, field, value) => {
    const updated = [...quickAddItems];
    updated[index][field] = value;
    setQuickAddItems(updated);
  };

  const removeQuickAddRow = (index) => {
    setQuickAddItems(quickAddItems.filter((_, i) => i !== index));
  };

  // Group menu items by category
  const menuByCategory = menuItems.reduce((acc, item) => {
    const cat = item.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const aiEnabledCount = menuItems.filter(item => item.ai_price_enabled).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="food-provider-dashboard">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Header */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Menu Items</p>
                  <p className="text-2xl font-bold">{menuItems.length}</p>
                </div>
                <Utensils className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Orders</p>
                  <p className="text-2xl font-bold">{orders.filter(o => !['delivered', 'cancelled'].includes(o.status)).length}</p>
                </div>
                <Package className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">AI Pricing</p>
                  <p className="text-2xl font-bold">{aiEnabledCount}</p>
                </div>
                <Sparkles className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Orders</p>
                  <p className="text-2xl font-bold">{orders.length}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab('menu')}
            className={`px-4 py-2 font-medium ${activeTab === 'menu' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            <Utensils className="w-4 h-4 inline mr-2" />
            Menu Management
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-2 font-medium ${activeTab === 'orders' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            <Package className="w-4 h-4 inline mr-2" />
            Orders
            {orders.filter(o => o.status === 'pending').length > 0 && (
              <Badge className="ml-2 bg-red-500">{orders.filter(o => o.status === 'pending').length}</Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 font-medium ${activeTab === 'analytics' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Analytics
          </button>
        </div>

        {/* Menu Tab */}
        {activeTab === 'menu' && (
          <div>
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Menu Item
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Add Menu Item</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddItem} className="space-y-4">
                    <div>
                      <Label>Item Name *</Label>
                      <Input
                        value={newItem.name}
                        onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                        placeholder="e.g., Grilled Chicken Sandwich"
                        required
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={newItem.description}
                        onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                        placeholder="Describe the dish..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Category</Label>
                        <Select value={newItem.category} onValueChange={(v) => setNewItem({...newItem, category: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map(cat => (
                              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Price *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newItem.price}
                          onChange={(e) => setNewItem({...newItem, price: e.target.value})}
                          placeholder="12.99"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Prep Time (minutes)</Label>
                      <Input
                        type="number"
                        value={newItem.prep_time}
                        onChange={(e) => setNewItem({...newItem, prep_time: parseInt(e.target.value)})}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-purple-600" />
                        <div>
                          <p className="font-medium text-sm">AI Price Monitoring</p>
                          <p className="text-xs text-gray-500">Auto-adjust prices based on market</p>
                        </div>
                      </div>
                      <Switch
                        checked={newItem.ai_price_enabled}
                        onCheckedChange={(v) => setNewItem({...newItem, ai_price_enabled: v})}
                      />
                    </div>
                    <Button type="submit" className="w-full">Add Item</Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={showQuickAdd} onOpenChange={setShowQuickAdd}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-blue-600 text-blue-600">
                    <Zap className="w-4 h-4 mr-2" />
                    Quick Add Multiple
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Quick Add Menu Items</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    {quickAddItems.map((item, index) => (
                      <div key={index} className="flex gap-2 items-center p-3 bg-gray-50 rounded-lg">
                        <Input
                          placeholder="Item name"
                          value={item.name}
                          onChange={(e) => updateQuickAddRow(index, 'name', e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Price"
                          value={item.price}
                          onChange={(e) => updateQuickAddRow(index, 'price', e.target.value)}
                          className="w-24"
                        />
                        <Select 
                          value={item.category} 
                          onValueChange={(v) => updateQuickAddRow(index, 'category', v)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map(cat => (
                              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => updateQuickAddRow(index, 'ai_price_enabled', !item.ai_price_enabled)}
                          className={item.ai_price_enabled ? 'text-purple-600' : 'text-gray-400'}
                        >
                          <Sparkles className="w-4 h-4" />
                        </Button>
                        {quickAddItems.length > 1 && (
                          <Button variant="ghost" size="icon" onClick={() => removeQuickAddRow(index)}>
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button variant="outline" onClick={addQuickAddRow} className="w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Another Row
                    </Button>
                    <Button onClick={handleQuickAdd} className="w-full bg-blue-600">
                      Add {quickAddItems.filter(i => i.name && i.price).length} Items
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {aiEnabledCount > 0 && (
                <Button
                  variant="outline"
                  className="border-purple-600 text-purple-600"
                  onClick={() => handleAiPriceUpdate()}
                  disabled={aiUpdating}
                >
                  {aiUpdating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  AI Update Prices ({aiEnabledCount})
                </Button>
              )}
            </div>

            {/* Menu Items by Category */}
            {Object.keys(menuByCategory).length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Utensils className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Menu Items Yet</h3>
                  <p className="text-gray-500 mb-4">Add your first menu item to get started</p>
                  <Button onClick={() => setShowAddItem(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Item
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {CATEGORIES.filter(cat => menuByCategory[cat.value]?.length > 0).map(category => (
                  <div key={category.value}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <category.icon className="w-5 h-5" />
                      {category.label}
                      <Badge variant="secondary">{menuByCategory[category.value].length}</Badge>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {menuByCategory[category.value].map(item => (
                        <Card key={item.item_id} className="overflow-hidden">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h4 className="font-semibold text-gray-900">{item.name}</h4>
                                <p className="text-sm text-gray-500 line-clamp-2">{item.description}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-green-600">${item.price?.toFixed(2)}</p>
                                {item.ai_price_enabled && (
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">
                                    <Sparkles className="w-3 h-3 mr-1" />
                                    AI
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-3 pt-3 border-t">
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Clock className="w-4 h-4" />
                                {item.prep_time} min
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingItem(item)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteItem(item.item_id)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-4">
            {orders.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">No Orders Yet</h3>
                  <p className="text-gray-500">Orders will appear here when customers place them</p>
                </CardContent>
              </Card>
            ) : (
              orders.map(order => (
                <Card key={order.order_id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold">Order #{order.order_id.slice(-6)}</h4>
                        <p className="text-sm text-gray-500">{order.customer_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">${order.total?.toFixed(2)}</p>
                        <Badge className={
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          order.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                          order.status === 'preparing' ? 'bg-purple-100 text-purple-700' :
                          order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }>
                          {order.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      {order.items?.map((item, i) => (
                        <span key={i}>
                          {item.quantity}x {item.name}
                          {i < order.items.length - 1 && ', '}
                        </span>
                      ))}
                    </div>
                    {order.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600"
                          onClick={() => handleOrderStatusUpdate(order.order_id, 'confirmed')}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-600"
                          onClick={() => handleOrderStatusUpdate(order.order_id, 'cancelled')}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Decline
                        </Button>
                      </div>
                    )}
                    {order.status === 'confirmed' && (
                      <Button
                        size="sm"
                        onClick={() => handleOrderStatusUpdate(order.order_id, 'preparing')}
                      >
                        Start Preparing
                      </Button>
                    )}
                    {order.status === 'preparing' && (
                      <Button
                        size="sm"
                        onClick={() => handleOrderStatusUpdate(order.order_id, 'ready')}
                      >
                        Mark Ready
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <Card>
            <CardContent className="py-12 text-center">
              <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">Analytics Coming Soon</h3>
              <p className="text-gray-500">View detailed sales and performance metrics</p>
            </CardContent>
          </Card>
        )}

        {/* Edit Item Dialog */}
        <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Menu Item</DialogTitle>
            </DialogHeader>
            {editingItem && (
              <div className="space-y-4">
                <div>
                  <Label>Item Name</Label>
                  <Input
                    value={editingItem.name}
                    onChange={(e) => setEditingItem({...editingItem, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={editingItem.description || ''}
                    onChange={(e) => setEditingItem({...editingItem, description: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Select 
                      value={editingItem.category} 
                      onValueChange={(v) => setEditingItem({...editingItem, category: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingItem.price}
                      onChange={(e) => setEditingItem({...editingItem, price: parseFloat(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-sm">AI Price Monitoring</span>
                  </div>
                  <Switch
                    checked={editingItem.ai_price_enabled}
                    onCheckedChange={(v) => setEditingItem({...editingItem, ai_price_enabled: v})}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-sm">Available</span>
                  <Switch
                    checked={editingItem.is_available}
                    onCheckedChange={(v) => setEditingItem({...editingItem, is_available: v})}
                  />
                </div>
                <Button onClick={handleUpdateItem} className="w-full">Save Changes</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
